import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import ActiveDatasetsDisplay from '../common/activeDatasets';
import ThinkingStreamPanel from '../common/thinkingStreamPanel';
import ChatInput from './chatInput';
import ChatMessage from './chatMessage';
import { useChat } from '../../context/chatContext';
import type { ChatMessage as ChatMessageType } from '../../types';

const PAGE_SIZE = 50;
const CLAMP_END = Number.MAX_SAFE_INTEGER; // backend should clamp to total

const ChatView: React.FC = () => {


  const [total, setTotal] = useState(0);
  const [winStart, setWinStart] = useState<number>(0);
  const [winEnd, setWinEnd] = useState<number>(0);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [initialized, setInitialized] = useState(false);
const { activeChatId, isLoading, sendMessage, getVisibleMessages, loadOlderForActive, userId, token } = useChat();

const messages = getVisibleMessages();
const scrollRef = useRef<HTMLDivElement>(null);

// Top-load older when near top
const handleScroll = useCallback(async () => {
  const el = scrollRef.current;
  if (!el) return;
  if (el.scrollTop <= 48) {
    const prevHeight = el.scrollHeight;
    await loadOlderForActive();
    requestAnimationFrame(() => {
      const newHeight = el.scrollHeight;
      el.scrollTop = el.scrollTop + (newHeight - prevHeight);
    });
  }
}, [loadOlderForActive]);

useEffect(() => {
  const el = scrollRef.current;
  if (!el) return;
  el.addEventListener('scroll', handleScroll, { passive: true });
  return () => el.removeEventListener('scroll', handleScroll);
}, [handleScroll]);

  // Auto-stick to bottom on new incoming messages (when already at bottom)
  const lastCountRef = useRef<number>(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !initialized) return;
    const atBottom = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 12;
    const grew = messages.length > lastCountRef.current;
    if (grew && atBottom) {
      el.scrollTop = el.scrollHeight;
    }
    lastCountRef.current = messages.length;
  }, [messages.length, initialized]);

  // If no chat open
  if (!activeChatId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        Select a chat or start a new one
      </Box>
    );
  }

  return (
    <>
      <ActiveDatasetsDisplay />

      {/* Scroll container */}
      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: { xs: 0, sm: 2 },
          py: { xs: 1, sm: 2 },
        }}
      >
       <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto', px: { xs: 0, sm: 2 }, py: { xs: 1, sm: 2 } }}>
  {messages.map((msg: any) => (
    <ChatMessage key={msg.id ?? msg.client_message_id} message={msg} />
  ))}
</Box>

      </Box>

      {(userId && activeChatId && token && isLoading) && (
        <ThinkingStreamPanel
          token={token}
          defaultExpanded
          maxHeight={320}
          userId={userId}
          chatId={activeChatId}
        />
      )}

      <ChatInput onSendMessage={sendMessage} isLoading={isLoading} />
    </>
  );
};

export default ChatView;
